import { Capacitor } from '@capacitor/core';

/**
 * Build-time flag: true if this bundle was built with GATSBY_CAPACITOR_BUILD=true
 * (via `yarn build-cap-dev/staging/production`). Fixed at build; does not vary
 * at runtime. Prefer this for SSR/hydration/branching decisions that must be
 * stable across the whole app lifecycle.
 *
 * Distinct from `isNativePlatform()`, which runtime-detects whether we're
 * actually executing inside a Capacitor native container.
 *
 * NOTE FOR TESTING: This is a `const` (not a function), which means
 * consumers' `import { isCapacitorBuild }` destructures it once at module
 * load. To toggle the value between tests we rely on Babel's ES-module
 * interop emitting property access (`_platform.isCapacitorBuild`) at every
 * reference site, so a `jest.mock(..., () => ({ get isCapacitorBuild() {...} }))`
 * getter re-evaluates per access. This works under the project's current
 * Babel config but is load-bearing — see the getter pattern in
 * `src/utils/__tests__/platform.test.ts`,
 * `src/components/blog/__tests__/BlogCard.test.tsx`, and
 * `src/pages/__tests__/IndexPage.test.tsx`. If a future Babel/TS upgrade
 * switches to imported-binding semantics, those tests will silently
 * always test the load-time value. Long-term cleanup: refactor to a
 * function call (`isCapacitorBuild()`) so the value is read at call site.
 */
export const isCapacitorBuild = process.env.GATSBY_CAPACITOR_BUILD === 'true';

export function getTargetAddressBase(): string {
  return (
    process.env.GATSBY_TARGET_ADDRESS || 'https://www.dentalpost.net'
  ).replace(/\/+$/, '');
}

export function isNativePlatform(): boolean {
  if (typeof window === 'undefined') return false;
  return Capacitor.isNativePlatform();
}

export function getPlatform(): 'ios' | 'android' | 'web' {
  if (typeof window === 'undefined') return 'web';
  return Capacitor.getPlatform() as 'ios' | 'android' | 'web';
}

export function isIOS(): boolean {
  return getPlatform() === 'ios';
}

export function isAndroid(): boolean {
  return getPlatform() === 'android';
}

export function openInCapacitorBrowser(path: string): void {
  const base = getTargetAddressBase();

  // Resolve the requested path against the trusted base origin. Reject any
  // input that escapes that origin (absolute URL with a different scheme/host,
  // protocol-relative `//evil.tld/...`, etc.). Algolia search hits and other
  // upstream callers can carry author-controlled URLs — a stray cross-origin
  // value would otherwise render in the in-app browser with DentalPost
  // chrome around it, an effective phishing surface.
  let target: URL;
  try {
    target = new URL(path, `${base}/`);
  } catch {
    console.warn('[openInCapacitorBrowser] invalid path rejected:', path);
    return;
  }
  const baseOrigin = new URL(base).origin;
  if (target.origin !== baseOrigin) {
    console.warn(
      '[openInCapacitorBrowser] cross-origin path rejected:',
      path,
      '->',
      target.origin
    );
    return;
  }

  // Fail loudly rather than degrading to window.open. In a Capacitor WebView
  // window.open either nukes the app shell (replaces the view with the
  // target URL) or is a no-op depending on platform — both worse than a
  // visible error.
  import('@capacitor/browser')
    .then(({ Browser }) => Browser.open({ url: target.toString() }))
    .catch((err) => {
      console.error(
        '[openInCapacitorBrowser] failed to open in Capacitor browser:',
        err
      );
    });
}
